import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, Stack, IconButton, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { ServiceGrid } from '../grids';
import { AddServiceDialog, ServiceUploadHelpDialog } from '../dialogs';
import ServiceUploadCsv from '../commands/ServiceUploadCsv';
import ServiceDownloadCsv from '../commands/ServiceDownloadCsv';
import { useGetServicesQuery } from '../../../../services/ConfigurationService';
import { UploadProgress } from '../../../../services/signalRService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const ServicesTab: React.FC = () => {
  const { id: centerId } = useParams<{ id: string }>();
  const query = useQuery();
  const company = query.get("company") || "";
  
  // Fetch services data
  const { data: services = [], refetch: refetchServices } = useGetServicesQuery({ centerId: centerId || '' }, { skip: !centerId });
  
  // Upload progress state
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Upload handlers
  const handleUploadStart = () => {
    setProgress({
      operationId: '',
      progressPercentage: 0,
      message: 'Starting upload...',
      processedRecords: 0,
      totalRecords: 0,
      timestamp: new Date().toISOString(),
      status: 'Starting',
      uploadType: 'services'
    });
  };
  
  const handleProgressUpdate = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadComplete = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadError = (error: string) => {
    console.error('Service upload error:', error);
    setProgress(null);
  };
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Tooltip title="View upload file specification">
          <IconButton 
            onClick={() => setHelpDialogOpen(true)}
            size="small"
            color="primary"
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        <ServiceDownloadCsv
          company={company}
          centerId={centerId || ''}
          onError={(error) => console.error('Download error:', error)}
        />
        <ServiceUploadCsv
          company={company}
          centerId={centerId || ''}
          onUploadStart={handleUploadStart}
          onProgressUpdate={handleProgressUpdate}
          onComplete={handleUploadComplete}
          onSuccess={() => {
            refetchServices();
            console.log('Services uploaded successfully');
          }}
          onError={handleUploadError}
        />
        <AddServiceDialog
          defaultCenterId={centerId || ''}
          onSuccess={refetchServices}
        />
      </Box>
      <ServiceGrid
        services={services}
        centerId={centerId || ''}
        onServiceSaved={refetchServices}
      />
      
      {/* Help Dialog */}
      <ServiceUploadHelpDialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </Stack>
  );
};